/**
 * Composable para manejar unidades de peso (Kg/Lb) de forma global
 * Guarda la preferencia del usuario en localStorage
 */

import { ref, computed } from 'vue';

// Estado global compartido entre todas las instancias
const globalWeightUnit = ref('kg'); // 'kg' o 'lb'
const STORAGE_KEY = 'preferred_weight_unit';

// Cargar preferencia del localStorage al iniciar
const loadPreference = () => {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved && (saved === 'kg' || saved === 'lb')) {
    globalWeightUnit.value = saved;
  }
};

// Cargar preferencia inicial
loadPreference();

export function useWeightUnits() {
  const isKg = computed(() => globalWeightUnit.value === 'kg');
  const isLb = computed(() => globalWeightUnit.value === 'lb');

  /**
   * Cambiar entre Kg y Lb
   */
  const toggleUnit = () => {
    globalWeightUnit.value = globalWeightUnit.value === 'kg' ? 'lb' : 'kg';
    localStorage.setItem(STORAGE_KEY, globalWeightUnit.value);
  };

  /**
   * Establecer unidad específica
   */
  const setUnit = (unit) => {
    if (unit === 'kg' || unit === 'lb') {
      globalWeightUnit.value = unit;
      localStorage.setItem(STORAGE_KEY, unit);
    }
  };

  /**
   * Convertir peso de Kg a Lb
   */
  const kgToLb = (kg) => {
    if (!kg || isNaN(kg)) return 0;
    return kg * 2.20462;
  };

  /**
   * Convertir peso de Lb a Kg
   */
  const lbToKg = (lb) => {
    if (!lb || isNaN(lb)) return 0;
    return lb / 2.20462;
  };

  /**
   * Parsear peso en formato "kg.g" (ej: "75.500" = 75kg 500g)
   */
  const parseKgWeight = (value) => {
    if (!value) return 0;
    const [kg, g] = String(value).split('.').map(Number);
    return kg + ((g || 0) / 1000);
  };

  /**
   * Formatear peso en Kg con gramos (75.5 kg → "75.500")
   */
  const formatKgWeight = (totalKg) => {
    if (!totalKg || isNaN(totalKg)) return '';
    const kg = Math.floor(totalKg);
    const grams = Math.round((totalKg % 1) * 1000);
    return `${kg}.${String(grams).padStart(3, '0')}`;
  };

  /**
   * Convertir valor actual al otro sistema
   */
  const convertWeight = (value, fromUnit) => {
    if (!value) return '';
    
    if (fromUnit === 'kg') {
      const totalKg = parseKgWeight(value);
      const lb = kgToLb(totalKg);
      return lb.toFixed(2);
    } else {
      const lb = parseFloat(value);
      const totalKg = lbToKg(lb);
      return formatKgWeight(totalKg);
    }
  };

  /**
   * Obtener label de la unidad actual
   */
  const currentUnitLabel = computed(() => {
    return globalWeightUnit.value === 'kg' ? 'Kg' : 'Lb';
  });

  /**
   * Obtener label del botón de conversión
   */
  const toggleButtonLabel = computed(() => {
    return globalWeightUnit.value === 'kg' ? 'Cambiar a Libras' : 'Cambiar a Kilogramos';
  });

  /**
   * Obtener placeholder según unidad
   */
  const weightPlaceholder = computed(() => {
    return globalWeightUnit.value === 'kg' ? 'kg.g (ej: 75.500)' : 'lb (ej: 165.35)';
  });

  return {
    // Estado
    globalWeightUnit,
    isKg,
    isLb,
    
    // Métodos
    toggleUnit,
    setUnit,
    kgToLb,
    lbToKg,
    parseKgWeight,
    formatKgWeight,
    convertWeight,
    
    // Computed
    currentUnitLabel,
    toggleButtonLabel,
    weightPlaceholder
  };
}
